import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../api/client';

// Tiny markdown -> HTML (headings, bold, italic, lists, paragraphs, links)
const mdToHtml = (md = '') => {
  let html = md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.*$)/gim, '<h3 class="serif-italic text-2xl text-[#d4af37] mt-8 mb-3">$1</h3>')
    .replace(/^## (.*$)/gim, '<h2 class="serif-italic gold-gradient-text text-3xl mt-10 mb-4">$1</h2>')
    .replace(/^# (.*$)/gim, '<h1 class="serif-italic gold-gradient-text text-4xl mt-10 mb-5">$1</h1>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-zinc-100">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="text-[#d4af37]">$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#d4af37] underline hover:text-[#f5c518]">$1</a>')
    .replace(/^- (.*$)/gim, '<li class="text-zinc-300 ml-4 list-disc">$1</li>');
  // Wrap consecutive li in ul
  html = html.replace(/(<li[^>]*>.*?<\/li>\s*)+/gs, (m) => `<ul class="my-3 space-y-1">${m}</ul>`);
  // Paragraphs (split by blank line)
  html = html.split(/\n{2,}/).map((blk) => {
    if (/^<h\d|^<ul|^<li/.test(blk.trim())) return blk;
    if (!blk.trim()) return '';
    return `<p class="text-zinc-300 leading-relaxed">${blk.replace(/\n/g, '<br />')}</p>`;
  }).join('\n');
  return html;
};

const Page = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getPage(slug).then(setPage).catch(() => setPage(null)).finally(() => setLoading(false));
  }, [slug]);

  return (
    <>
      <Header />
      <main className="bg-black min-h-[60vh]">
        {loading && <div className="max-w-3xl mx-auto px-6 py-20 text-zinc-500">Loading…</div>}
        {!loading && !page && (
          <div className="max-w-3xl mx-auto px-6 py-20 text-center">
            <h1 className="serif-italic gold-gradient-text text-4xl">Page not found</h1>
            <Link to="/" className="text-[#d4af37] underline mt-4 inline-block">Back home</Link>
          </div>
        )}
        {page && (
          <article className={`mx-auto px-6 py-16 ${page.template === 'fullwidth' ? 'max-w-6xl' : 'max-w-3xl'}`}>
            <p className="text-[#d4af37] tracking-[0.4em] text-xs uppercase mb-3">{page.template === 'faq' ? 'FAQ' : page.template === 'contact' ? 'Get in touch' : 'Page'}</p>
            <h1 className="serif-italic gold-gradient-text text-5xl md:text-6xl">{page.title}</h1>
            {page.excerpt && <p className="text-zinc-400 text-lg mt-4 leading-relaxed">{page.excerpt}</p>}
            <div className="mt-10 prose-invert space-y-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(mdToHtml(page.content), { USE_PROFILES: { html: true } }) }} />
            {page.template === 'contact' && (
              <div className="mt-12 grid md:grid-cols-2 gap-6">
                <div className="border border-zinc-900 rounded p-6">
                  <h3 className="serif-italic text-[#d4af37] text-xl mb-3">Send us a message</h3>
                  <form onSubmit={(e) => { e.preventDefault(); }} className="space-y-3">
                    <input type="email" placeholder="Your email" className="w-full bg-transparent border border-zinc-800 rounded px-3 py-2 text-zinc-200" required />
                    <textarea rows={4} placeholder="Your message" className="w-full bg-transparent border border-zinc-800 rounded px-3 py-2 text-zinc-200" required />
                    <button type="submit" className="btn-gold rounded px-5 h-10 text-sm font-medium">Send</button>
                  </form>
                </div>
                <div className="border border-zinc-900 rounded p-6 text-zinc-300">
                  <h3 className="serif-italic text-[#d4af37] text-xl mb-3">Or reach us directly</h3>
                  <p className="text-sm">Email: <a className="text-[#d4af37]" href="mailto:hello@wesupplysmoke.com">hello@wesupplysmoke.com</a></p>
                  <p className="text-sm mt-2">Phone / WhatsApp: <span className="text-zinc-100">+31 (0)20 000 0000</span></p>
                  <p className="text-zinc-500 text-xs mt-4">We typically reply within 2 hours during business hours.</p>
                </div>
              </div>
            )}
          </article>
        )}
      </main>
      <Footer />
    </>
  );
};

export default Page;
